import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MySchedule() {
	const [schedule, setSchedule] = useState([]);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadSchedule();
	}, [currentDate]);

	const loadSchedule = async () => {
		try {
			const year = currentDate.getFullYear();
			const month = currentDate.getMonth();
			const startDate = new Date(year, month, 1)
				.toISOString()
				.split("T")[0];
			const endDate = new Date(year, month + 1, 0)
				.toISOString()
				.split("T")[0];

			const response = await axios.get(
				`${API_URL}/schedules/my-schedule?startDate=${startDate}&endDate=${endDate}`,
			);
			setSchedule(response.data);
		} catch (error) {
			console.error("Failed to load schedule:", error);
		} finally {
			setLoading(false);
		}
	};

	const getMonthDates = () => {
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);

		const startPadding = firstDay.getDay();
		const dates = [];

		for (let i = startPadding - 1; i >= 0; i--) {
			const date = new Date(year, month, -i);
			dates.push({ date, isCurrentMonth: false });
		}

		for (let day = 1; day <= lastDay.getDate(); day++) {
			dates.push({
				date: new Date(year, month, day),
				isCurrentMonth: true,
			});
		}

		const endPadding = (7 - (dates.length % 7)) % 7;
		for (let i = 1; i <= endPadding; i++) {
			dates.push({
				date: new Date(year, month + 1, i),
				isCurrentMonth: false,
			});
		}

		return dates;
	};

	const getAssignmentForDate = (date) => {
		const dateStr = date.toISOString().split("T")[0];
		return schedule.find((s) => s.schedule_date === dateStr);
	};

	const prevMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
		);
	};

	const nextMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
		);
	};

	const formatMonthYear = () => {
		return currentDate.toLocaleDateString("en-US", {
			month: "long",
			year: "numeric",
		});
	};

	const isToday = (date) => {
		const today = new Date();
		return date.toDateString() === today.toDateString();
	};

	const isWeekend = (date) => {
		const day = date.getDay();
		return day === 0 || day === 6;
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	const monthDates = getMonthDates();
	const totalShifts = schedule.length;
	const weekendShifts = schedule.filter((s) => s.is_weekend).length;

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>My Schedule</h1>
			</div>

			<div className='stats-grid' style={{ marginBottom: "1rem" }}>
				<div className='stat-card'>
					<div className='stat-label'>Shifts This Month</div>
					<div className='stat-value'>{totalShifts}</div>
				</div>
				<div className='stat-card'>
					<div className='stat-label'>Weekend Shifts</div>
					<div className='stat-value'>{weekendShifts}</div>
				</div>
				<div className='stat-card'>
					<div className='stat-label'>Hours Scheduled</div>
					<div className='stat-value'>{totalShifts * 8}</div>
				</div>
			</div>

			<div className='card'>
				<div className='flex justify-between items-center mb-md'>
					<button className='btn btn-secondary' onClick={prevMonth}>
						← Previous
					</button>
					<h2 style={{ margin: 0 }}>{formatMonthYear()}</h2>
					<button className='btn btn-secondary' onClick={nextMonth}>
						Next →
					</button>
				</div>

				<div className='schedule-grid'>
					{DAYS_OF_WEEK.map((day) => (
						<div key={day} className='schedule-header'>
							{day}
						</div>
					))}
					{monthDates.map(({ date, isCurrentMonth }, idx) => {
						const assignment = getAssignmentForDate(date);
						return (
							<div
								key={idx}
								className={`schedule-cell ${isWeekend(date) ? "weekend" : ""} ${isToday(date) ? "today" : ""}`}
								style={{
									opacity: isCurrentMonth ? 1 : 0.4,
									minHeight: "80px",
								}}
							>
								<div className='schedule-date'>
									{date.getDate()}
								</div>
								{assignment && (
									<div
										className='schedule-assignment'
										style={{
											background: "var(--primary-500)",
											color: "white",
											padding: "4px 8px",
											textAlign: "center",
										}}
									>
										{assignment.department_name}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			<div className='card'>
				<div className='card-header'>
					<h3>Shifts List</h3>
				</div>
				{schedule.length === 0 ? (
					<p
						className='text-muted text-center'
						style={{ padding: "1rem" }}
					>
						No shifts scheduled for this month.
					</p>
				) : (
					<div className='table-container'>
						<table className='table'>
							<thead>
								<tr>
									<th>Date</th>
									<th>Day</th>
									<th>Department</th>
									<th>Time</th>
								</tr>
							</thead>
							<tbody>
								{schedule.map((s, idx) => (
									<tr
										key={idx}
										style={{
											background: s.is_weekend
												? "var(--warning-light)"
												: "transparent",
										}}
									>
										<td className='font-medium'>
											{new Date(
												s.schedule_date,
											).toLocaleDateString("en-US", {
												month: "short",
												day: "numeric",
											})}
										</td>
										<td>
											{
												DAYS_OF_WEEK[
													new Date(
														s.schedule_date,
													).getDay()
												]
											}
										</td>
										<td>
											<span className='badge badge-primary'>
												{s.department_name}
											</span>
										</td>
										<td>
											{s.shift_start} - {s.shift_end}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

export default MySchedule;
